/* My access as an explorer (Tony, 21 Oct 2026). Left: the person's folders as a tree, like
 * Drive's sidebar. Right: the selected folder — where it sits, who owns it, what you can do
 * there, and the folders inside it. On a phone only the right pane shows and you drill down.
 * State language as everywhere: orange is only ever an action (Add files in Drive); can-read is ink;
 * a level you pass through is grey and dashed, never red. Selection is state, so it is ink. */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import type { AccessFolder, ReverseLookup } from '../types'
import { Pill } from './ui'
import { ancestors, filterIds, openCount, usefulDescription, type ExplorerNode } from '../routes/myAccessTree'
import { FolderFiles } from './FolderFiles'
import { showsFiles } from './fileRows'

function FolderIcon() {
  return (
    <svg className="ma-ico" viewBox="0 0 20 16" aria-hidden="true">
      <path d="M1.5 3.2c0-.9.7-1.7 1.7-1.7h4.4l1.8 2h7.4c.9 0 1.7.8 1.7 1.7v7.6c0 .9-.8 1.7-1.7 1.7H3.2c-1 0-1.7-.8-1.7-1.7z"
        fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className={`ma-chev${open ? ' ma-chev--open' : ''}`} viewBox="0 0 12 12" aria-hidden="true">
      <path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const canFile = (f?: AccessFolder) => f?.state === 'visible' && f.access === 'edit'

/* ---------------------------------------------------------------- tree */

export function FolderTreePane({ roots, selectedId, onSelect, query: outerQuery, canSelect, label = 'Your folders', pickLabel }: {
  roots: ExplorerNode[]
  selectedId: string
  onSelect: (id: string) => void
  /** Move a folder (30 Oct 2026): the page's own box filters, so the pane shows no filter of its own. */
  query?: string
  /** Rows that cannot be picked open and close instead of selecting. Default: every row selects. */
  canSelect?: (n: ExplorerNode) => boolean
  label?: string
  /** A picker (Move a folder): pickable rows get this button, and clicking a row only opens it. */
  pickLabel?: string
}) {
  const [innerQuery, setQuery] = useState('')
  const query = outerQuery ?? innerQuery
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const focusRef = useRef<HTMLDivElement | null>(null)
  const [focusId, setFocusId] = useState(selectedId)
  /* Move real focus only for arrow-key moves, never on load or a click elsewhere. */
  const byKeyboard = useRef(false)

  const index = useMemo(() => {
    const m = new Map<string, ExplorerNode>()
    const walk = (n: ExplorerNode) => { m.set(n.id, n); n.children.forEach(walk) }
    roots.forEach(walk)
    return m
  }, [roots])

  /* Selecting anything (here, in the right pane, or by Back) opens the way to it. */
  useEffect(() => {
    const node = index.get(selectedId)
    if (!node) return
    setExpanded((prev) => {
      const next = new Set(prev)
      for (const a of ancestors(node)) next.add(a.id)
      if (node.kind === 'drive') next.add(node.id)
      return next
    })
    setFocusId(selectedId)
  }, [selectedId, index])

  const keep = useMemo(() => filterIds(roots, query), [roots, query])
  const isOpen = (n: ExplorerNode) => (keep ? true : expanded.has(n.id))

  /* The rows on screen, in order: what the arrow keys walk. */
  const visible: { node: ExplorerNode; depth: number }[] = []
  const walk = (nodes: ExplorerNode[], depth: number) => {
    for (const n of nodes) {
      if (keep && !keep.has(n.id)) continue
      visible.push({ node: n, depth })
      if (n.children.length && isOpen(n)) walk(n.children, depth + 1)
    }
  }
  walk(roots, 0)

  const toggle = (id: string, open?: boolean) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (open ?? !next.has(id)) next.add(id)
      else next.delete(id)
      return next
    })

  const choose = (n: ExplorerNode) => {
    if (!pickLabel && (!canSelect || canSelect(n))) onSelect(n.id)
    else if (n.children.length && !keep) toggle(n.id)
  }

  useEffect(() => {
    if (byKeyboard.current) focusRef.current?.focus()
    byKeyboard.current = false
  }, [focusId])

  const onKey = (e: KeyboardEvent, n: ExplorerNode) => {
    byKeyboard.current = true
    const i = visible.findIndex((v) => v.node.id === n.id)
    const move = (j: number) => { const v = visible[Math.max(0, Math.min(visible.length - 1, j))]; if (v) setFocusId(v.node.id) }
    if (e.key === 'ArrowDown') move(i + 1)
    else if (e.key === 'ArrowUp') move(i - 1)
    else if (e.key === 'Home') move(0)
    else if (e.key === 'End') move(visible.length - 1)
    else if (e.key === 'ArrowRight') {
      if (n.children.length && !isOpen(n)) toggle(n.id, true)
      else if (n.children.length) setFocusId(n.children.find((c) => !keep || keep.has(c.id))?.id ?? n.id)
    } else if (e.key === 'ArrowLeft') {
      if (n.children.length && isOpen(n) && !keep) toggle(n.id, false)
      else if (n.parent) setFocusId(n.parent.id)
    } else if (e.key === 'Enter' || e.key === ' ') choose(n)
    else return
    e.preventDefault()
  }

  return (
    <nav className="ma-treepane" aria-label={label}>
      {outerQuery === undefined && (
        <input className="ma-filter" type="search" placeholder="Filter folders" aria-label="Filter folders"
          value={query} onChange={(e) => setQuery(e.target.value)} />
      )}
      {keep && keep.size === 0 && <p className="ma-empty">No folder you can reach matches “{query}”.</p>}
      <div role="tree" aria-label={label} className="ma-tree">
        {visible.map(({ node: n, depth }) => {
          const open = isOpen(n)
          /* While filtering, a row whose folders are all filtered out shows no arrow. */
          const hasKids = keep ? n.children.some((c) => keep.has(c.id)) : n.children.length > 0
          const selected = n.id === selectedId
          return (
            <div key={n.id} role="treeitem" aria-level={depth + 1} aria-selected={selected}
              aria-expanded={hasKids ? open : undefined}
              tabIndex={n.id === focusId ? 0 : -1} ref={n.id === focusId ? focusRef : undefined}
              className={`ma-node${selected ? ' ma-node--on' : ''}${n.pass ? ' ma-node--pass' : ''}${n.kind === 'drive' ? ' ma-node--drive' : ''}`}
              style={{ paddingLeft: 6 + depth * 16 }}
              onClick={() => { setFocusId(n.id); choose(n) }}
              onKeyDown={(e) => onKey(e, n)}>
              {hasKids ? (
                <span className="ma-twisty" onClick={(e) => { e.stopPropagation(); toggle(n.id) }} aria-hidden="true">
                  <Chevron open={open} />
                </span>
              ) : <span className="ma-twisty" aria-hidden="true" />}
              {n.kind === 'drive' ? <span className="ma-code">{n.drive.code}</span> : <FolderIcon />}
              <span className="ma-node__nm">{n.kind === 'drive' ? n.name.replace(/^\d+\s+RMS\s+/, '') : n.name}</span>
              {n.folder?.provisional && <span className="ma-new">New</span>}
              {n.folder && canFile(n.folder) && <span className="ma-dot" title="You can file here" aria-label="You can file here" />}
              {pickLabel && (!canSelect || canSelect(n)) && (
                <button className="ma-pick" aria-label={`${pickLabel}: ${n.name}`}
                  onClick={(e) => { e.stopPropagation(); onSelect(n.id) }}
                  onKeyDown={(e) => e.stopPropagation()}>
                  {pickLabel}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </nav>
  )
}

/* -------------------------------------------------------------- detail */

/* The heading is the whole path, Level 1 → Level 2 → Level 3 (Tony, 21 Oct): earlier levels are
   links back up (grey when you only pass through), the selected folder is last and bold. */
function PathTitle({ node, onSelect }: { node: ExplorerNode; onSelect: (id: string) => void }) {
  const trail = ancestors(node)
  return (
    <h2 className="ma-path">
      {trail.map((a) => (
        <span key={a.id} className="ma-path__step">
          {a.kind === 'drive' ? (
            <button className="ma-path__up" onClick={() => onSelect(a.id)} title={a.name}>
              <span className="ma-code">{a.drive.code}</span>
            </button>
          ) : (
            <button className={`ma-path__up${a.pass ? ' ma-path__up--pass' : ''}`} onClick={() => onSelect(a.id)}
              title={a.pass ? 'No access at this level' : undefined}>
              {a.name}
            </button>
          )}
          <span className="ma-path__sep" aria-hidden="true">→</span>
        </span>
      ))}
      <span className="ma-path__here" aria-current="page"><FolderIcon />{node.name}</span>
    </h2>
  )
}

function ChildList({ node, onSelect }: { node: ExplorerNode; onSelect: (id: string) => void }) {
  if (!node.children.length) return null
  return (
    <section className="ma-children" aria-label={node.kind === 'drive' ? 'Folders' : 'Folders inside'}>
      <h3 className="ma-children__h">{node.kind === 'drive' ? 'Folders' : 'Folders inside'}</h3>
      <ul>
        {node.children.map((c) => {
          const n = openCount(c)
          return (
            <li key={c.id} className={`ma-child${c.pass ? ' ma-child--pass' : ''}`}>
              <button className="ma-child__go" onClick={() => onSelect(c.id)}>
                <FolderIcon />
                <span className="ma-child__nm">{c.name}</span>
                {c.folder?.provisional && <span className="ma-new">New</span>}
                {c.pass ? <span className="ma-tag">No access at this level</span>
                  : c.folder?.state === 'unknown' ? <Pill tone="amber">Could not check</Pill>
                  : n > 0 ? <span className="ma-count">{n} inside</span> : null}
                <Chevron open={false} />
              </button>
              {c.folder?.state === 'visible' && (
                <a className="ma-child__act" href={c.folder.webViewLink} target="_blank" rel="noreferrer"
                  aria-label={`Open in Drive: ${c.name}`}>
                  Open <span aria-hidden="true">↗</span>
                </a>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export function FolderDetail({ node, onSelect, getReverse, moveEnabled = false, filesEnabled = false }: {
  node: ExplorerNode
  onSelect: (id: string) => void
  getReverse: (folderId: string) => Promise<ReverseLookup | null>
  /** Move a folder (29 Oct 2026): offer "Move a folder here" on folders this person can add to. */
  moveEnabled?: boolean
  /** Folder files (3 Nov 2026): list the files directly inside an openable folder. */
  filesEnabled?: boolean
}) {
  const [who, setWho] = useState<{ id: string; data: ReverseLookup | null } | null>(null)
  useEffect(() => setWho(null), [node.id])
  const f = node.folder
  const back = node.parent

  return (
    <div className="ma-detail">
      {back && (
        <button className="ma-back" onClick={() => onSelect(back.id)}>
          <span aria-hidden="true">‹</span> {back.kind === 'drive' ? back.name.replace(/^\d+\s+RMS\s+/, '') : back.name}
        </button>
      )}

      {node.kind === 'drive' ? (
        <header className="ma-drivehead">
          <h2><span className="ma-code">{node.drive.code}</span>{node.name}</h2>
          <Pill>{node.drive.confidentiality}</Pill>
          <p className="ma-meta">{openCount(node)} folders you can open in this drive.</p>
        </header>
      ) : node.pass ? (
        <header className="ma-panel ma-panel--pass">
          <PathTitle node={node} onSelect={onSelect} />
          <p className="ma-meta">You cannot open this folder. You reach the folders below through it.</p>
        </header>
      ) : f ? (
        <header className={`ma-panel ${f.state === 'unknown' ? 'ma-panel--unknown' : canFile(f) ? 'ma-panel--file' : 'ma-panel--read'}`}>
          <PathTitle node={node} onSelect={onSelect} />
          {f.grantedBy && <p className="ma-grp">{f.grantedBy}</p>}
          {usefulDescription(f) && <p className="ma-desc">{usefulDescription(f)}</p>}
          <p className="ma-meta">Owner: {f.provisional ? 'pending — new folder' : f.accountableOwner}</p>
          {f.state === 'unknown' ? (
            <p className="ma-meta"><Pill tone="amber">Could not check</Pill> Google did not answer for this folder. This is not the same as no access.</p>
          ) : (
            <div className="ma-actions">
              <a className={`btn ${canFile(f) ? 'btn--primary' : 'btn--secondary'}`} href={f.webViewLink} target="_blank" rel="noreferrer">
                {canFile(f) ? 'Add files in Drive' : 'Open in Drive'} <span aria-hidden="true">↗</span>
              </a>
              {/* A provisional folder is not in the catalogue yet, so neither lookup knows it. */}
              {!f.provisional && <button className="btn btn--secondary" aria-expanded={!!who}
                onClick={async () => {
                  if (who) { setWho(null); return }
                  const id = f.accessFrom ?? f.folderId
                  setWho({ id, data: await getReverse(id) })
                }}>
                Who else can see this?
              </button>}
              {moveEnabled && canFile(f) && !f.provisional && (
                <Link className="btn btn--secondary" to={`/move-folder?to=${encodeURIComponent(f.folderId)}`}>Move a folder here</Link>
              )}
            </div>
          )}
          {who && (
            <div className="ma-who">
              {f.accessFrom && <p className="ma-meta">Same people as {who.data?.folderName ?? 'the folder above'}.</p>}
              {who.data ? (
                <table>
                  <thead><tr><th>Group</th><th>Role</th><th style={{ textAlign: 'right' }}>People</th></tr></thead>
                  <tbody>
                    {who.data.groups.map((g) => (
                      <tr key={g.email}>
                        <td className="mono" style={{ fontSize: '.76rem' }}>{g.email}</td>
                        <td>{g.role === 'writer' ? 'Can edit' : 'Can read'}</td>
                        <td className="num">{g.memberCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="ma-meta">Could not load who else can see this folder.</p>}
            </div>
          )}
        </header>
      ) : null}

      <ChildList node={node} onSelect={onSelect} />
      {f && showsFiles(node, filesEnabled) && <FolderFiles folderId={f.folderId} />}
    </div>
  )
}
